// Frontend-only seeder for VR session demo data.
//
// Generates 524 sessions across Ultimeta agency's 4 projects by calling
// the existing /.netlify/functions/vr-session-start + /vr-session-complete
// endpoints — same path real sessions take, no service-token plumbing or
// backend cooperation needed.
//
// Trigger from the browser DevTools console while logged in as super_admin:
//
//   await window.seedDemoSessions()
//
// Caveat: backend assigns timestamps at session-create time, so every
// seeded record gets a "just now" timestamp. Pin display / clustering /
// sublocality / visitor filters all test fine; the date-range filter
// won't have spread.

import { startVrSession, completeVrSession } from "rc-app/api/vrSessionApi";

// ---------- IDs pulled from Ultimeta stage backend ----------

const AGENCY_ID = "6a0eed9532077d388e71492c"; // Ultimeta Advisory
const AGENCY_SLUG = "ultimeta";
const ABA_ID = "6a0eefa732077d388e714930";    // Sivaram Pandravada

const AGENT_IDS = [
  "6a11d863d0d2f45cddc2db11", // Arjun Agarwal  (A145)
  "6a118680d0d2f45cddc2db06", // Amar Kokkanti  (A144)
  "6a10112bd4fbb6bcccc20151", // Roshan D'Souza (A140)
];

type ProjectConfig = {
  project_id: string;
  project_slug: string;
  twins: string[];
};

const PROJECTS: Record<string, ProjectConfig> = {
  opensky_italia: {
    project_id: "6a115f67d0d2f45cddc2daf7",
    project_slug: "italia",
    twins: [
      "bv9cjHCa1Qr", // Corso Firenze
      "DvDJkWP7fWp", // Vilale Paradiso
      "Rd5uV7RSdkk", // Portofino
      "VTjUoCxTCyz", // Senzo Titilo
    ],
  },
  ruchira: {
    project_id: "6a14232ad0d2f45cddc2dbc1",
    project_slug: "ruchira-feliz",
    twins: ["dePvdP83C9K"],
  },
  nandanavanam: {
    project_id: "6a0fd08632077d388e714956",
    project_slug: "nanda",
    twins: ["qB7r7nCEmEJ"],
  },
  kohinoor: {
    project_id: "6a0fd8fa32077d388e714973",
    project_slug: "kohinoor",
    twins: ["R6WgfSs2LgC"],
  },
};

type LocationConfig = {
  city: string;
  lat: number;
  lng: number;
  radiusKm: number;
  region: string;
  country: string;
  countryCode: string;
};

// Urban centres — modest radius, dense pin scatter
const LOCATIONS: Record<string, LocationConfig> = {
  Genoa:     { city: "Genoa",     lat:  44.4056, lng:   8.9463, radiusKm:  8, region: "Liguria",          country: "Italy",     countryCode: "IT" },
  Rio:       { city: "Rio de Janeiro", lat: -22.9068, lng: -43.1729, radiusKm: 15, region: "Rio de Janeiro", country: "Brazil",  countryCode: "BR" },
  Brasilia:  { city: "Brasilia",  lat: -15.7975, lng: -47.8919, radiusKm: 18, region: "Distrito Federal", country: "Brazil",    countryCode: "BR" },
  Dubai:     { city: "Dubai",     lat:  25.2048, lng:  55.2708, radiusKm: 12, region: "Dubai",            country: "UAE",       countryCode: "AE" },
  "Abu Dhabi": { city: "Abu Dhabi", lat: 24.4539, lng: 54.3773, radiusKm: 12, region: "Abu Dhabi",       country: "UAE",       countryCode: "AE" },
  Hyderabad: { city: "Hyderabad", lat:  17.385,  lng:  78.4867, radiusKm: 15, region: "Telangana",        country: "India",     countryCode: "IN" },
  Bangalore: { city: "Bangalore", lat:  12.9716, lng:  77.5946, radiusKm: 15, region: "Karnataka",        country: "India",     countryCode: "IN" },
  Tirupati:  { city: "Tirupati",  lat:  13.6288, lng:  79.4192, radiusKm:  8, region: "Andhra Pradesh",   country: "India",     countryCode: "IN" },
  Delhi:     { city: "Delhi",     lat:  28.7041, lng:  77.1025, radiusKm: 18, region: "Delhi",            country: "India",     countryCode: "IN" },
};

// Rural pseudo-regions — wide spread of pins
const RURAL_LOCATIONS: Record<string, LocationConfig> = {
  Tuscany:       { city: "Tuscany",       lat:  43.3,  lng:  11.3, radiusKm: 40, region: "Tuscany",        country: "Italy",     countryCode: "IT" },
  "Minas Gerais":{ city: "Minas Gerais",  lat: -19.2,  lng: -44.0, radiusKm: 50, region: "Minas Gerais",   country: "Brazil",    countryCode: "BR" },
  Pampas:        { city: "Pampas",        lat: -34.5,  lng: -60.0, radiusKm: 60, region: "Buenos Aires",   country: "Argentina", countryCode: "AR" },
  "Rural KA/AP": { city: "Rural KA/AP",   lat:  14.5,  lng:  78.0, radiusKm: 60, region: "Andhra Pradesh", country: "India",     countryCode: "IN" },
  "Outer Hyd":   { city: "Outer Hyderabad", lat: 17.4, lng:  78.7, radiusKm: 30, region: "Telangana",      country: "India",     countryCode: "IN" },
};

// Nandanavanam concentrates at Hitech City (Madhapur) — overrides the
// regular "Hyderabad" entry for that project only.
const HITECH_CITY: LocationConfig = {
  city: "Hyderabad",
  lat: 17.4475,
  lng: 78.3796,
  radiusKm: 3,
  region: "Telangana",
  country: "India",
  countryCode: "IN",
};

// Distribution: (project, location, count). Totals: 347 + 95 + 50 + 30 = 522.
type DistEntry = { project: keyof typeof PROJECTS; location: string; count: number };

const DISTRIBUTION: DistEntry[] = [
  // OpenSky Italia — international portfolio
  { project: "opensky_italia", location: "Genoa",        count:  33 },
  { project: "opensky_italia", location: "Rio",          count: 105 },
  { project: "opensky_italia", location: "Brasilia",     count: 134 },
  { project: "opensky_italia", location: "Dubai",        count:  25 },
  { project: "opensky_italia", location: "Abu Dhabi",    count:  25 },
  // 25 rural sessions across IT/BR/AR
  { project: "opensky_italia", location: "Tuscany",      count:   8 },
  { project: "opensky_italia", location: "Minas Gerais", count:   9 },
  { project: "opensky_italia", location: "Pampas",       count:   8 },

  // Ruchira Villa Feliz — India-focused
  { project: "ruchira", location: "Hyderabad",   count: 20 },
  { project: "ruchira", location: "Bangalore",   count: 38 },
  { project: "ruchira", location: "Tirupati",    count: 15 },
  { project: "ruchira", location: "Delhi",       count: 12 },
  { project: "ruchira", location: "Rural KA/AP", count: 10 },

  // Kohinoor by Auro — Hyderabad concentration
  { project: "kohinoor", location: "Hyderabad", count: 40 },
  { project: "kohinoor", location: "Outer Hyd", count: 10 },

  // Nandanavanam — Hitech City cluster (uses HITECH_CITY override)
  { project: "nandanavanam", location: "Hyderabad", count: 30 },
];

// Duration buckets — weighted to land on ~8-9 min average per spec.
const DURATION_BUCKETS: Array<[number, number, number]> = [
  // [minSeconds, maxSeconds, weight]
  [   5,   59, 0.05], // Quick exit
  [  60,  120, 0.10], // Brief look
  [ 240,  300, 0.15], // Short tour
  [ 480,  600, 0.50], // Standard tour (avg target)
  [ 600, 1800, 0.20], // Deep dive
];

// ---------- Helpers ----------

const randInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const weightedPick = <T>(items: T[], weights: number[]): T => {
  const total = weights.reduce((s, w) => s + w, 0);
  let r = Math.random() * total;
  for (let i = 0; i < items.length; i++) {
    r -= weights[i];
    if (r <= 0) return items[i];
  }
  return items[items.length - 1];
};

const jitterCoord = (
  centerLat: number,
  centerLng: number,
  radiusKm: number,
): { lat: number; lng: number } => {
  // sqrt(uniform) for uniform area distribution; theta uniform.
  const rKm = Math.sqrt(Math.random()) * radiusKm;
  const theta = Math.random() * 2 * Math.PI;
  const dxKm = rKm * Math.cos(theta);
  const dyKm = rKm * Math.sin(theta);
  const dlat = dyKm / 111.0;
  const dlng = dxKm / (111.0 * Math.max(0.01, Math.cos((centerLat * Math.PI) / 180)));
  return { lat: centerLat + dlat, lng: centerLng + dlng };
};

const randomDuration = (): number => {
  const weights = DURATION_BUCKETS.map(([, , w]) => w);
  const [lo, hi] = weightedPick(DURATION_BUCKETS, weights);
  return randInt(lo, hi);
};

type VisitorRoles = {
  clientManagerId: string;
  clientAdminId: string;
  agentId: string;
  customerId: string;
  userEmail: string;
};

const randomVisitor = (): VisitorRoles => {
  // Backend requires at least one of aba_id/agent_id/asa_id/customer_id — no truly anon sessions.
  // Redistribute: 30% ABA (Sivaram), 70% one of the three agents.
  const role = weightedPick(["aba", "agent"], [0.30, 0.70]);
  if (role === "aba") {
    return {
      clientManagerId: ABA_ID,
      clientAdminId: "",
      agentId: "",
      customerId: "",
      userEmail: "sales@ultimetavr.com",
    };
  }
  return {
    clientManagerId: "",
    clientAdminId: "",
    agentId: AGENT_IDS[randInt(0, AGENT_IDS.length - 1)],
    customerId: "",
    userEmail: "agent@ultimetavr.com",
  };
};

const pickLocation = (dist: DistEntry): LocationConfig => {
  if (dist.project === "nandanavanam" && dist.location === "Hyderabad") {
    return HITECH_CITY;
  }
  return LOCATIONS[dist.location] || RURAL_LOCATIONS[dist.location];
};

// ---------- Main seed function ----------

type SeedResult = {
  attempted: number;
  started: number;
  completed: number;
  errors: Array<{ index: number; phase: "start" | "complete"; message: string }>;
};

const CONCURRENCY = 8;

const seedOne = async (
  index: number,
  dist: DistEntry,
  result: SeedResult,
): Promise<void> => {
  result.attempted += 1;
  const project = PROJECTS[dist.project];
  const loc = pickLocation(dist);
  if (!loc) {
    result.errors.push({
      index,
      phase: "start",
      message: `Unknown location ${dist.location}`,
    });
    return;
  }
  const twin = project.twins[randInt(0, project.twins.length - 1)];
  const { lat, lng } = jitterCoord(loc.lat, loc.lng, loc.radiusKm);
  const visitor = randomVisitor();
  const duration = randomDuration();
  const usedVr = Math.random() < 0.10;
  const vrTime = usedVr ? randInt(Math.floor(duration * 0.3), Math.floor(duration * 0.9)) : 0;

  // Start payload mirrors buildStartPayload + geo enrichment.
  const startPayload = {
    matterport_id: twin,
    project_id: project.project_id,
    agency_id: AGENCY_ID,
    experience_type: "mp" as const,
    agency_slug: AGENCY_SLUG,
    project_slug: project.project_slug,
    experience_slug: twin,
    aba_id: visitor.clientManagerId ? [visitor.clientManagerId] : [],
    asa_id: visitor.clientAdminId ? [visitor.clientAdminId] : [],
    agent_id: visitor.agentId ? [visitor.agentId] : [],
    customer_id: visitor.customerId ? [visitor.customerId] : [],
    user_id: visitor.userEmail,
    device: weightedPick(["desktop", "mobile"], [0.55, 0.45]),
    referrer_website: "",
    // `src` now means share-link channel (whatsapp/copy/qr/...). Seeded
    // sessions are synthetic — set null = "organic / no channel".
    src: null as string | null,
    vr_type: "self exploratory" as const,
    // Geo enrichment — omit country/region to avoid backend country-list
    // validation (backend rejects country names not in its lookup table).
    latitude: lat,
    longitude: lng,
    city: loc.city,
  };

  let sessionId: string | null = null;
  try {
    const res: any = await startVrSession(startPayload);
    sessionId = res?.session_id || null;
    if (!sessionId) {
      result.errors.push({
        index,
        phase: "start",
        message: "No session_id in start response",
      });
      return;
    }
    result.started += 1;
  } catch (err: any) {
    const detail = err?.response?.data ?? err?.message ?? String(err);
    console.error("[seed] start 422 detail:", JSON.stringify(detail, null, 2));
    result.errors.push({
      index,
      phase: "start",
      message: err?.message || String(err),
    });
    return;
  }

  // Complete payload — bypass the rich zone/VR state machinery and just
  // ship the aggregate fields the backend needs to flip status=completed.
  const completePayload = {
    session_id: sessionId,
    matterport_id: twin,
    agency_id: AGENCY_ID,
    aba_id: visitor.clientManagerId ? [visitor.clientManagerId] : [],
    asa_id: visitor.clientAdminId ? [visitor.clientAdminId] : [],
    agent_id: visitor.agentId ? [visitor.agentId] : [],
    customer_id: visitor.customerId ? [visitor.customerId] : [],
    total_duration: duration,
    zones_visited: [] as string[],
    zone_times: {} as Record<string, number>,
    most_viewed_zone: "",
    most_viewed_time: 0,
    total_nodes: 0,
    used_vr: usedVr,
    vr_time: vrTime,
    type: "mp" as const,
    vr_type: "self exploratory" as const,
  };

  try {
    await completeVrSession(completePayload);
    result.completed += 1;
  } catch (err: any) {
    result.errors.push({
      index,
      phase: "complete",
      message: err?.message || String(err),
    });
  }
};

const seedDemoSessions = async (): Promise<SeedResult> => {
  // Build the flat task list: 522 entries from the distribution + 2 edge
  // cases (placeholder 0,0 and null/null lat/lng) at the end.
  const tasks: DistEntry[] = [];
  for (const dist of DISTRIBUTION) {
    for (let i = 0; i < dist.count; i++) tasks.push(dist);
  }

  const result: SeedResult = {
    attempted: 0,
    started: 0,
    completed: 0,
    errors: [],
  };

  // eslint-disable-next-line no-console
  console.log(
    `[seedDemoSessions] starting — ${tasks.length} sessions, ${CONCURRENCY}-way parallel`,
  );
  const startedAt = Date.now();

  // Process in CONCURRENCY-sized batches. Promise.all per batch is simpler
  // than a worker-queue and good enough for ~500 items.
  for (let i = 0; i < tasks.length; i += CONCURRENCY) {
    const batch = tasks.slice(i, i + CONCURRENCY);
    await Promise.all(batch.map((dist, j) => seedOne(i + j, dist, result)));
    if ((i + CONCURRENCY) % 40 === 0 || i + CONCURRENCY >= tasks.length) {
      // eslint-disable-next-line no-console
      console.log(
        `[seedDemoSessions] progress: ${Math.min(i + CONCURRENCY, tasks.length)}/${tasks.length}` +
          ` (started=${result.started}, completed=${result.completed}, errors=${result.errors.length})`,
      );
    }
  }

  // Edge cases — single sequential pair so they don't get lost in batch noise.
  const edgeCases: Array<{ label: string; latitude: any; longitude: any }> = [
    { label: "placeholder (0,0)", latitude: 0, longitude: 0 },
    { label: "null/null fallback", latitude: null, longitude: null },
  ];
  for (const ec of edgeCases) {
    result.attempted += 1;
    const project = PROJECTS.ruchira;
    const twin = project.twins[0];
    const visitor = randomVisitor();
    const startPayload = {
      matterport_id: twin,
      project_id: project.project_id,
      agency_id: AGENCY_ID,
      experience_type: "mp" as const,
      agency_slug: AGENCY_SLUG,
      project_slug: project.project_slug,
      experience_slug: twin,
      aba_id: visitor.clientManagerId ? [visitor.clientManagerId] : ([] as string[]),
      asa_id: visitor.clientAdminId ? [visitor.clientAdminId] : ([] as string[]),
      agent_id: visitor.agentId ? [visitor.agentId] : ([] as string[]),
      customer_id: [] as string[],
      user_id: "edge-case@example.com",
      device: "desktop",
      referrer_website: "",
      src: "http://localhost:8000/seed",
      vr_type: "self exploratory" as const,
      latitude: ec.latitude,
      longitude: ec.longitude,
      city: "Hyderabad",
      region: "Telangana",
      country: "India",
      country_code: "IN",
    };
    try {
      const res: any = await startVrSession(startPayload);
      const sessionId = res?.session_id;
      if (!sessionId) {
        result.errors.push({
          index: -1,
          phase: "start",
          message: `Edge case ${ec.label}: no session_id`,
        });
        continue;
      }
      result.started += 1;
      await completeVrSession({
        session_id: sessionId,
        matterport_id: twin,
        agency_id: AGENCY_ID,
        aba_id: [] as string[],
        asa_id: [] as string[],
        agent_id: [] as string[],
        customer_id: [] as string[],
        total_duration: 540,
        zones_visited: [] as string[],
        zone_times: {} as Record<string, number>,
        most_viewed_zone: "",
        most_viewed_time: 0,
        total_nodes: 0,
        used_vr: false,
        vr_time: 0,
        type: "mp" as const,
        vr_type: "self exploratory" as const,
      });
      result.completed += 1;
    } catch (err: any) {
      result.errors.push({
        index: -1,
        phase: "start",
        message: `Edge case ${ec.label}: ${err?.message || String(err)}`,
      });
    }
  }

  const elapsedSec = ((Date.now() - startedAt) / 1000).toFixed(1);
  // eslint-disable-next-line no-console
  console.log(
    `[seedDemoSessions] done in ${elapsedSec}s — attempted=${result.attempted}` +
      `, started=${result.started}, completed=${result.completed}, errors=${result.errors.length}`,
  );
  if (result.errors.length) {
    // eslint-disable-next-line no-console
    console.warn("[seedDemoSessions] first 10 errors:", result.errors.slice(0, 10));
  }
  return result;
};

// Expose to DevTools. Single attachment at module import time; importing
// this file once (from index.tsx) makes window.seedDemoSessions available
// everywhere on the app.
if (typeof window !== "undefined") {
  (window as any).seedDemoSessions = seedDemoSessions;
}

export default seedDemoSessions;
